// src/components/admin/flows/panels/NodeConfigPanel.js
import React from 'react';
import { X, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useFlowAutomation } from '../../../../contexts/FlowAutomationContext';
import { useTheme } from '../../../../contexts/ThemeContext';
import { NODE_TYPES } from '../canvas/nodeRegistry';
import TriggerConfigForm from './TriggerConfigForm';
import ConditionConfigForm from './ConditionConfigForm';
import ActionConfigForm from './ActionConfigForm';
import ApprovalConfigForm from './ApprovalConfigForm';
import DelayConfigForm from './DelayConfigForm';

const NodeConfigPanel = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const {
        selectedNode,
        selectedNodeId,
        setSelectedNodeId,
        updateStep,
        removeStep,
    } = useFlowAutomation();

    if (!selectedNode) return null;

    const nodeType = NODE_TYPES[selectedNode.type];

    const handleConfigChange = (config) => {
        updateStep(selectedNodeId, { config });
    };

    const handleLabelChange = (label) => {
        updateStep(selectedNodeId, { label });
    };

    const handleDelete = () => {
        if (selectedNode.type === 'trigger') return; // Can't delete trigger
        removeStep(selectedNodeId);
    };

    const renderConfigForm = () => {
        switch (selectedNode.type) {
            case 'trigger':
                return <TriggerConfigForm config={selectedNode.config} onChange={handleConfigChange} />;
            case 'condition':
                return <ConditionConfigForm config={selectedNode.config} onChange={handleConfigChange} />;
            case 'action':
                return <ActionConfigForm config={selectedNode.config} onChange={handleConfigChange} />;
            case 'approval':
                return <ApprovalConfigForm config={selectedNode.config} onChange={handleConfigChange} />;
            case 'delay':
                return <DelayConfigForm config={selectedNode.config} onChange={handleConfigChange} />;
            case 'end':
                return (
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        This node ends the workflow. No configuration needed.
                    </p>
                );
            default:
                return null;
        }
    };

    return (
        <motion.div
            initial={{ x: 320 }}
            animate={{ x: 0 }}
            exit={{ x: 320 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`w-80 flex-shrink-0 border-l overflow-y-auto ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
        >
            {/* Panel Header */}
            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2">
                    {nodeType && (
                        <div
                            className="w-7 h-7 rounded-md flex items-center justify-center"
                            style={{ backgroundColor: nodeType.color + '20' }}
                        >
                            <nodeType.icon className="w-4 h-4" style={{ color: nodeType.color }} />
                        </div>
                    )}
                    <span className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {nodeType?.label || 'Configure'}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    {selectedNode.type !== 'trigger' && (
                        <button
                            onClick={handleDelete}
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                            title="Delete node"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                    <button
                        onClick={() => setSelectedNodeId(null)}
                        className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Panel Body */}
            <div className="p-4 space-y-4">
                {/* Label */}
                <div>
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Label
                    </label>
                    <input
                        type="text"
                        value={selectedNode.label || ''}
                        onChange={(e) => handleLabelChange(e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg text-sm border ${
                            isDark
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                        } focus:ring-2 focus:ring-blue-500 focus:outline-none`}
                    />
                </div>

                {/* Config Form */}
                {renderConfigForm()}
            </div>
        </motion.div>
    );
};

export default NodeConfigPanel;
